import { memo, useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, TextField as MuiTextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import { ButtonV1, DropdownV1, DatePickerV1 } from "components";
import { ErrorMsg } from "CustomStyledComponents";
import { getMaxFromDate, getMaxToDate, getDateRangeError } from "utils";

import {
  DEFAULT_FILTER_VALUES,
  UPLOAD_NAME,
  UPLOADED_BY_ALL_OPTION,
  FROM_DATE_ERROR,
  TO_DATE_ERROR,
} from "../constants";
import { buildFilterRequestPayload } from "../makeData";

const FIELD_SX = { flex: "1 1 180px", minWidth: 160 };

const StatusHistoryFilter = ({
  uploadedByOptions,
  fileTypeOptions,
  onSubmit,
  onClear,
}) => {
  const [dateError, setDateError] = useState("");

  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: DEFAULT_FILTER_VALUES,
  });

  const uploadedFrom = watch("uploadedFrom");
  const uploadedTo = watch("uploadedTo");

  const uploadedByFilterOptions = useMemo(
    () => [
      { value: UPLOADED_BY_ALL_OPTION, label: "All" },
      ...uploadedByOptions.map((name) => ({ value: name, label: name })),
    ],
    [uploadedByOptions],
  );

  const uploadNameFilterOptions = useMemo(
    () => [
      { value: UPLOAD_NAME.ALL, label: "All" },
      ...fileTypeOptions.map((type) => ({ value: type, label: type })),
    ],
    [fileTypeOptions],
  );

  const maxFromDate = useMemo(() => getMaxFromDate(uploadedTo), [uploadedTo]);
  const maxToDate = useMemo(() => getMaxToDate(uploadedFrom), [uploadedFrom]);

  const handleFilterSubmit = useCallback(
    (formValues) => {
      const rangeError = getDateRangeError(
        formValues.uploadedFrom,
        formValues.uploadedTo,
      );

      if (rangeError) {
        setDateError(rangeError);
        return;
      }

      setDateError("");
      onSubmit(buildFilterRequestPayload(formValues));
    },
    [onSubmit],
  );

  const handleClear = useCallback(() => {
    reset(DEFAULT_FILTER_VALUES);
    setDateError("");
    onClear();
  }, [reset, onClear]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleFilterSubmit)}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "flex-start",
        gap: 2,
        border: "1px solid",
        borderColor: "grey.300",
        borderRadius: 2,
        padding: 3,
        mb: 3,
      }}
    >
      <Controller
        name="search"
        control={control}
        render={({ field }) => (
          <MuiTextField
            {...field}
            size="small"
            label="Search (Request ID / File)"
            placeholder="REQ-... or file name"
            sx={FIELD_SX}
          />
        )}
      />

      <Box sx={FIELD_SX}>
        <Controller
          name="uploadName"
          control={control}
          render={({ field }) => (
            <DropdownV1
              {...field}
              label="Upload Type"
              itemList={uploadNameFilterOptions}
              listType="object"
              valueKey="value"
              displayKey="label"
            />
          )}
        />
      </Box>

      <Box sx={FIELD_SX}>
        <Controller
          name="uploadedBy"
          control={control}
          render={({ field }) => (
            <DropdownV1
              {...field}
              label="Uploaded By"
              itemList={uploadedByFilterOptions}
              listType="object"
              valueKey="value"
              displayKey="label"
            />
          )}
        />
      </Box>

      <Box sx={FIELD_SX}>
        <Controller
          name="uploadedFrom"
          control={control}
          render={({ field }) => (
            <DatePickerV1
              value={field.value}
              onChange={field.onChange}
              label="Uploaded from"
              disableFuture
              maxDate={maxFromDate}
              inputProps={{ placeholder: "dd-mm-yyyy" }}
              dateStyle={{ width: "100%" }}
            />
          )}
        />
        {dateError && <ErrorMsg>{FROM_DATE_ERROR}</ErrorMsg>}
      </Box>

      <Box sx={FIELD_SX}>
        <Controller
          name="uploadedTo"
          control={control}
          render={({ field }) => (
            <DatePickerV1
              value={field.value}
              onChange={field.onChange}
              label="Uploaded to"
              disableFuture
              minDate={uploadedFrom}
              maxDate={maxToDate}
              inputProps={{ placeholder: "dd-mm-yyyy" }}
              dateStyle={{ width: "100%" }}
            />
          )}
        />
        {dateError && <ErrorMsg>{TO_DATE_ERROR}</ErrorMsg>}
      </Box>

      <Box sx={{ display: "flex", gap: 1.5, ml: "auto", alignSelf: "center" }}>
        <ButtonV1 title="Submit" type="submit" variant="contained" />
        <ButtonV1 title="Clear" variant="outlined" onClick={handleClear} />
      </Box>
    </Box>
  );
};

StatusHistoryFilter.propTypes = {
  uploadedByOptions: PropTypes.arrayOf(PropTypes.string),
  fileTypeOptions: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};

StatusHistoryFilter.defaultProps = {
  uploadedByOptions: [],
  fileTypeOptions: [],
};

export default memo(StatusHistoryFilter);
